import * as R from 'ramda'
import { RSAA } from 'redux-api-middleware'
import { getApiPath, webStorage } from 'sidecar-utility-library'
import { makeQueryParams } from 'sidecar-utilities'

import { initialState, types } from './types'
import { getTzAdjustedStartDate } from 'utils/policies'
import { updateOrCreateObjectValueInArray } from '../utils/updateOrCreateObjectValueInArray'
import { applicantsToGenerateDocsFor } from './utils'

const env =
  {
    dev: 'sidecarhealth-dev-file-',
    qa: 'sidecarhealth-qa-file-',
    prod: 'sidecarhealth-prod-file-',
  }[process.env.APP_ENV] || 'sidecarhealth-dev-file-'

// ================= OFF EXCHANGE =====================

// we never estimate an application form so this
// is strictly for estimating a single applicants' policy
export const estimateSinglePolicyDoc = memberUuid => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `members/${memberUuid}/doc/policy`),
    method: 'POST',
    body: ({ Applicant, form }) => {
      const applicant = R.path(
        ['applicants', 'entities', memberUuid],
        Applicant,
      )

      // if there's no form, means this is coming
      // from fast track reapply where this call isn't
      // coming from a checkout step, in that case
      // we can bypass trying to access the form from redux
      if (!R.either(R.isEmpty, R.isNil)(form) && R.has('Checkout')(form)) {
        const applicantsFormValues = form.Checkout?.values

        return !R.either(R.isEmpty, R.isNil)(applicantsFormValues.applicants)
          ? // to make sure we pass the social security number
            // if we have it to generate the policy preview doc
            JSON.stringify([
              {
                ...applicant,
                paymentSchedule:
                  applicantsFormValues.paymentSchedule ||
                  applicant.paymentSchedule,
                ssn: applicantsFormValues.applicants[memberUuid].ssn,
                accountUuid: webStorage.getItem('aud'),
                quote: {
                  ...applicant.quote,
                  startDate: getTzAdjustedStartDate(applicant.quote),
                },
              },
            ])
          : JSON.stringify([
              {
                ...applicant,
                paymentSchedule:
                  applicantsFormValues.paymentSchedule ||
                  applicant.paymentSchedule,
                ssn: applicantsFormValues.ssn,
                accountUuid: webStorage.getItem('aud'),
                quote: {
                  ...applicant.quote,
                  startDate: getTzAdjustedStartDate(applicant.quote),
                },
              },
            ])
      }

      return JSON.stringify([
        {
          ...applicant,
          accountUuid: webStorage.getItem('aud'),
        },
      ])
    },
    types: [
      { type: types.ESTIMATE_POLICY_DOCS_REQUEST, meta: { uuid: memberUuid } },
      {
        type: types.ESTIMATE_POLICY_DOCS_SUCCESS,
        meta: { uuid: memberUuid },
      },
      { type: types.ESTIMATE_POLICY_DOCS_ERROR, meta: { uuid: memberUuid } },
    ],
  },
})

// generates the first docs you see in the submit your application modal
// before they have to be renewed due to AWS invalidations
export const generateFinalDocs = template => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `members/doc/${template}`),
    method: 'POST',
    body: ({ Applicant }) => {
      const applicants = R.pathOr([], ['applicants', 'entities'], Applicant)

      return JSON.stringify(applicantsToGenerateDocsFor(applicants))
    },
    types: [
      types.GENERATE_FINAL_DOCS_REQUEST,
      { type: types.GENERATE_FINAL_DOCS_SUCCESS, meta: { template } },
      types.GENERATE_FINAL_DOCS_ERROR,
    ],
  },
})

// generates both the first instance and the subsequent
// renewals of the upcoming policy docs
export const fetchAndRenewUpcomingPolicyDoc = upcomingPolicy => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `members/doc/renewUrl${makeQueryParams({
        file: `${upcomingPolicy.memberUuid}_${upcomingPolicy.quoteUuid}.pdf`,
        bucket: `${env}policy-preview`,
      })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_AND_RENEW_UPCOMING_POLICY_DOC_URL_REQUEST,
      {
        type: types.FETCH_AND_RENEW_UPCOMING_POLICY_DOC_URL_SUCCESS,
        meta: { uuid: upcomingPolicy.memberUuid },
      },
      types.FETCH_AND_RENEW_UPCOMING_POLICY_DOC_URL_ERROR,
    ],
  },
})

// fetches the policy docs for a member after they have purchased
// before the renewal calls fire because of AWS invalidations
export const fetchPolicyDocs = memUuid => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `members/${memUuid}/doc/policy`),
    method: 'GET',
    types: [
      types.FETCH_POLICY_DOCS_REQUEST,
      {
        type: types.FETCH_POLICY_DOCS_SUCCESS,
        meta: { uuid: memUuid },
      },
      types.FETCH_POLICY_DOCS_ERROR,
    ],
  },
})

// fetches the application doc for a member after they have purchased
// before the renewal calls fire because of AWS invalidations
export const fetchApplicationDocs = policyUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `members/policies/generatePdf${makeQueryParams({
        docType: 'form',
        policyUuid,
      })}`,
    ),
    method: 'POST',
    types: [
      types.FETCH_APPLICATION_DOCS_REQUEST,
      types.FETCH_APPLICATION_DOCS_SUCCESS,
      types.FETCH_APPLICATION_DOCS_ERROR,
    ],
  },
})

// fetches the application doc for a member after they have purchased
// before the renewal calls fire because of AWS invalidations
export const fetchPolicyAllBenefits = policyUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `members/policies/generatePdf${makeQueryParams({
        docType: 'policy',
        policyUuid,
      })}`,
    ),
    method: 'POST',
    types: [
      types.FETCH_POLICY_BENEFIT_DOCS_REQUEST,
      types.FETCH_POLICY_BENEFIT_DOCS_SUCCESS,
      types.FETCH_POLICY_BENEFIT_DOCS_ERROR,
    ],
  },
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.FETCH_APPLICATION_DOCS_REQUEST:
      return {
        ...state,
        applicationDoc: {
          ...state.applicationDoc,
          busy: true,
          error: '',
        },
      }
    case types.FETCH_POLICY_BENEFIT_DOCS_REQUEST:
      return {
        ...state,
        policyBenefitDoc: {
          ...state.policyBenefitDoc,
          busy: true,
          error: '',
        },
      }
    case types.FETCH_POLICY_DOCS_REQUEST:
    case types.GENERATE_FINAL_DOCS_REQUEST:
    case types.FETCH_AND_RENEW_UPCOMING_POLICY_DOC_URL_REQUEST:
      return {
        ...state,
        policyDocs: {
          ...state.policyDocs,
          busy: true,
          error: '',
        },
      }

    // -----------

    case types.FETCH_APPLICATION_DOCS_ERROR:
      return {
        ...state,
        applicationDoc: {
          ...state.applicationDoc,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }
    case types.FETCH_POLICY_BENEFIT_DOCS_ERROR:
      return {
        ...state,
        policyBenefitDoc: {
          ...state.policyBenefitDoc,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }

    case types.FETCH_POLICY_DOCS_ERROR:
    case types.GENERATE_FINAL_DOCS_ERROR:
    case types.FETCH_AND_RENEW_UPCOMING_POLICY_DOC_URL_ERROR:
      return {
        ...state,
        policyDocs: {
          ...state.policyDocs,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }

    // -----------

    case types.ESTIMATE_POLICY_DOCS_REQUEST: {
      const { uuid } = meta
      return {
        ...state,
        policyDocs: {
          ...state.policyDocs,
          previewPolicyBusy: uuid,
        },
      }
    }

    case types.ESTIMATE_POLICY_DOCS_SUCCESS: {
      const { uuid } = meta
      const errors = state.policyDocs.previewPolicyErrors

      return errors.includes(uuid)
        ? {
            ...state,
            policyDocs: {
              ...state.policyDocs,
              previewPolicyBusy: '',
              entities: updateOrCreateObjectValueInArray({
                array: state.policyDocs.entities,
                itemKey: 'uuid',
                targetValue: uuid,
                valueName: 'previewUrl',
                newValue: payload.url,
                newObject: { ...payload, uuid: uuid, previewUrl: payload.url },
              }),
              previewPolicyErrors: errors.filter(_uuid => _uuid !== uuid),
            },
          }
        : {
            ...state,
            policyDocs: {
              ...state.policyDocs,
              previewPolicyBusy: '',
              entities: updateOrCreateObjectValueInArray({
                array: state.policyDocs.entities,
                itemKey: 'uuid',
                targetValue: uuid,
                valueName: 'previewUrl',
                newValue: payload.url,
                newObject: { ...payload, uuid: uuid, previewUrl: payload.url },
              }),
            },
          }
    }

    case types.ESTIMATE_POLICY_DOCS_ERROR: {
      const { uuid } = meta
      const previousErrors = state.policyDocs.previewPolicyErrors

      return previousErrors.includes(uuid)
        ? {
            ...state,
            policyDocs: {
              ...state.policyDocs,
              previewPolicyBusy: '',
            },
          }
        : {
            ...state,
            policyDocs: {
              ...state.policyDocs,
              previewPolicyBusy: '',
              previewPolicyErrors: [...previousErrors, uuid],
            },
          }
    }

    // ----------

    case types.FETCH_POLICY_DOCS_SUCCESS: {
      const { uuid } = meta
      return {
        ...state,
        policyDocs: {
          ...state.policyDocs,
          busy: false,
          entities: updateOrCreateObjectValueInArray({
            array: state.policyDocs.entities,
            itemKey: 'uuid',
            targetValue: uuid,
            valueName: 'policyUrl',
            newValue: payload.url,
            newObject: { ...payload, uuid: uuid, policyUrl: payload.url },
          }),
        },
      }
    }

    // ----------

    case types.FETCH_APPLICATION_DOCS_SUCCESS:
      return {
        ...state,
        applicationDoc: {
          ...state.applicationDoc,
          busy: false,
          entity: payload.url,
        },
      }
    // ----------

    case types.FETCH_POLICY_BENEFIT_DOCS_SUCCESS:
      return {
        ...state,
        policyBenefitDoc: {
          ...state.policyBenefitDoc,
          busy: false,
          entity: payload.url,
        },
      }

    // ----------

    case types.GENERATE_FINAL_DOCS_SUCCESS: {
      const { template } = meta
      const previousState = state.policyDocs.combinedDocs
      const combinedDocUrlFile =
        template === 'form'
          ? {
              ...previousState,
              applicationUrl: payload.url,
              applicationFile: payload.file,
            }
          : {
              ...previousState,
              policyPreviewUrl: payload.url,
              policyPreviewFile: payload.file,
            }

      return {
        ...state,
        policyDocs: {
          ...state.policyDocs,
          busy: false,
          combinedDocs: combinedDocUrlFile,
        },
      }
    }

    // ---------

    case types.FETCH_AND_RENEW_UPCOMING_POLICY_DOC_URL_SUCCESS: {
      const { uuid } = meta
      return {
        ...state,
        policyDocs: {
          ...state.policyDocs,
          busy: false,
          entities: updateOrCreateObjectValueInArray({
            array: state.policyDocs.entities,
            itemKey: 'uuid',
            targetValue: uuid,
            valueName: 'upcomingPreviewUrl',
            newValue: payload.url,
            newObject: {
              ...payload,
              uuid: uuid,
              upcomingPreviewUrl: payload.url,
            },
          }),
        },
      }
    }

    default:
      return state
  }
}
